import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, query } from 'firebase/firestore';
import { FaPlus, FaEdit, FaTrash, FaBoxes, FaExclamationTriangle, FaTimes, FaSearch } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import Modal from 'react-modal';
import { ClipLoader } from 'react-spinners';
import useDebounce from './hooks/useDebounce';

// --- ESTILOS COMPARTIDOS KOKIMIKO ---
const formatCurrency = (value) => {
    const number = Number(value);
    if (isNaN(number)) return '0,00 €';
    return number.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

const cardStyle = "bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100";
const inputStyle = "w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-kokimiko-teal focus:outline-none text-sm font-bold text-kokimiko-texto";
const labelStyle = "block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2";

const customModalStyles = {
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
        marginRight: '-50%', transform: 'translate(-50%, -50%)',
        border: 'none', background: 'transparent', width: '100%', maxWidth: '600px', padding: '0'
    },
    overlay: { backgroundColor: 'rgba(35, 37, 89, 0.75)', zIndex: 1000 }
};

Modal.setAppElement('#root');

// --- FORMULARIO DE INVENTARIO KOKIMIKO REAL ---
function ItemFormModal({ item, onClose, onSave }) {
    const [formData, setFormData] = useState({
        nombre: item?.nombre || '',
        cantidad: item?.cantidad || 0,
        stockMinimo: item?.stockMinimo || 0,
        precio: item?.precio || 0,
        // 🔥 NUEVAS CATEGORÍAS REALES 🔥
        categoria: item?.categoria || 'talleres', 
        proveedor: item?.proveedor || ''
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseFloat(value) || 0 : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!formData.nombre.trim()) return toast.error("El nombre del artículo es obligatorio.");
        setIsSaving(true);
        await onSave(formData, item?.id, item?.categoria); 
        setIsSaving(false);
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-[2rem] shadow-2xl max-h-[90vh] overflow-y-auto font-sans">
            <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                <h3 className="text-xl font-black text-kokimiko-texto uppercase tracking-widest flex items-center gap-3">
                    <div className="p-3 bg-kokimiko-teal rounded-xl text-white"><FaBoxes size={18} /></div>
                    {item ? 'Editar Artículo' : 'Nuevo Artículo'}
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors p-2"><FaTimes size={24}/></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="md:col-span-2">
                    <label className={labelStyle}>Nombre del Artículo</label>
                    <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} className={inputStyle} placeholder="Ej: Pintura flúor, Café especialidad, Libro..." required />
                </div>
                
                <div className="md:col-span-2">
                    <label className={labelStyle}>Clasificación Operativa (Categoría)</label>
                    <select name="categoria" value={formData.categoria} onChange={handleChange} className={inputStyle} disabled={!!item}>
                        <option value="talleres">Consumibles de Talleres (Arcilla, Pintura, Slime)</option>
                        <option value="cafeteria">Insumos de Cafetería y Catering</option>
                        <option value="retail">Tienda Retail (Juguetes y Libros para venta)</option>
                        <option value="mobiliario">Equipos y Mobiliario Fijo</option>
                    </select>
                </div>
                
                <div className="md:col-span-2">
                    <label className={labelStyle}>Proveedor Habitual</label>
                    <input type="text" name="proveedor" value={formData.proveedor} onChange={handleChange} className={inputStyle} placeholder="Opcional" />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-8 bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div><label className={labelStyle}>Stock</label><input type="number" name="cantidad" value={formData.cantidad} onChange={handleChange} className={`${inputStyle} text-center`} min="0" required /></div>
                <div><label className={labelStyle}>Mínimo</label><input type="number" name="stockMinimo" value={formData.stockMinimo} onChange={handleChange} className={`${inputStyle} text-center`} min="0" /></div>
                <div><label className={labelStyle}>Precio Costo (€)</label><input type="number" name="precio" value={formData.precio} onChange={handleChange} className={`${inputStyle} text-right`} min="0" step="0.01" /></div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
                <button type="button" onClick={onClose} className="w-full sm:w-auto bg-gray-100 text-gray-500 font-black text-xs uppercase tracking-widest h-12 px-8 rounded-xl hover:bg-gray-200 transition-colors" disabled={isSaving}>Cancelar</button>
                <button type="submit" className="w-full sm:w-auto bg-kokimiko-teal text-white font-black text-xs uppercase tracking-widest h-12 px-8 rounded-xl shadow-md hover:opacity-80 transition-opacity" disabled={isSaving}>
                    {isSaving ? 'Guardando...' : 'Guardar Artículo'}
                </button>
            </div>
        </form>
    );
}

// --- VISTA PRINCIPAL UNIFICADA ---
function InventarioView() {
    const [inventario, setInventario] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const busquedaDebounced = useDebounce(busqueda, 300);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Leemos de la base de datos (Adaptando las colecciones viejas por ahora para no romper tus datos,
    // y creando las nuevas colecciones limpias)
    useEffect(() => {
        setCargando(true);
        // Colecciones Viejas (Legado)
        const qMateriaPrima = query(collection(db, "materiaPrima"));
        const qAccesorios = query(collection(db, "accesorios"));
        // Colecciones Nuevas Kokimiko
        const qTalleres = query(collection(db, "talleres"));
        const qCafeteria = query(collection(db, "cafeteria"));
        const qRetail = query(collection(db, "retail"));
        const qMobiliario = query(collection(db, "mobiliario"));

        let dataGlobal = [];

        const updateState = (newData, categoriaName) => {
            // Removemos los datos viejos de esta categoría
            dataGlobal = dataGlobal.filter(item => item.categoria !== categoriaName);
            // Añadimos los nuevos
            dataGlobal = [...dataGlobal, ...newData];
            
            const combinado = [...dataGlobal];
            combinado.sort((a, b) => a.nombre.localeCompare(b.nombre));
            setInventario(combinado);
            setCargando(false);
        };

        const unsubscribers = [
            onSnapshot(qMateriaPrima, (snap) => updateState(snap.docs.map(doc => ({ ...doc.data(), id: doc.id, categoria: 'materiaPrima' })), 'materiaPrima')),
            onSnapshot(qAccesorios, (snap) => updateState(snap.docs.map(doc => ({ ...doc.data(), id: doc.id, categoria: 'accesorios' })), 'accesorios')),
            onSnapshot(qTalleres, (snap) => updateState(snap.docs.map(doc => ({ ...doc.data(), id: doc.id, categoria: 'talleres' })), 'talleres')),
            onSnapshot(qCafeteria, (snap) => updateState(snap.docs.map(doc => ({ ...doc.data(), id: doc.id, categoria: 'cafeteria' })), 'cafeteria')),
            onSnapshot(qRetail, (snap) => updateState(snap.docs.map(doc => ({ ...doc.data(), id: doc.id, categoria: 'retail' })), 'retail')),
            onSnapshot(qMobiliario, (snap) => updateState(snap.docs.map(doc => ({ ...doc.data(), id: doc.id, categoria: 'mobiliario' })), 'mobiliario'))
        ];

        return () => unsubscribers.forEach(unsub => unsub());
    }, []);

    const inventarioFiltrado = useMemo(() => {
        if (!busquedaDebounced) return inventario;
        return inventario.filter(i => i.nombre?.toLowerCase().includes(busquedaDebounced.toLowerCase()));
    }, [inventario, busquedaDebounced]);

    const valorTotalInventario = inventario.reduce((total, item) => total + ((item.cantidad || 0) * (item.precio || 0)), 0);

    const handleSaveItem = async (itemData, itemId, oldCategoria) => {
        const toastId = toast.loading('Guardando...');
        try {
            const collectionName = itemData.categoria; 

            if (itemId) {
                // Actualizamos
                await updateDoc(doc(db, oldCategoria, itemId), itemData);
            } else {
                // Nuevo
                await addDoc(collection(db, collectionName), itemData);
            }
            toast.success('¡Artículo guardado exitosamente!', { id: toastId });
            setIsModalOpen(false);
            setEditingItem(null);
        } catch (error) {
            console.error("Error al guardar:", error);
            toast.error('No se pudo guardar el artículo.', { id: toastId });
        }
    };

    const handleDeleteItem = async (id, categoria) => {
        if (window.confirm("¿Seguro que deseas eliminar este artículo de forma permanente?")) {
            const toastId = toast.loading('Eliminando...');
            try {
                await deleteDoc(doc(db, categoria, id));
                toast.success('Artículo eliminado.', { id: toastId });
            } catch (error) {
                console.error("Error al eliminar:", error);
                toast.error('No se pudo eliminar.', { id: toastId });
            }
        }
    };

    const openModal = (item = null) => { setEditingItem(item); setIsModalOpen(true); };

    // Función para renderizar el badge de categoría con colores corporativos
    const renderCategoriaBadge = (cat) => {
        switch(cat) {
            case 'talleres': return <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md mt-1 inline-block bg-purple-100 text-purple-600">Talleres</span>;
            case 'cafeteria': return <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md mt-1 inline-block bg-orange-100 text-orange-600">Cafetería</span>;
            case 'retail': return <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md mt-1 inline-block bg-green-100 text-green-600">Retail / Tienda</span>;
            case 'mobiliario': return <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md mt-1 inline-block bg-blue-100 text-blue-600">Mobiliario</span>;
            default: return <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md mt-1 inline-block bg-gray-100 text-gray-500">Antiguo ({cat})</span>;
        }
    };

    return (
        <div className="h-full flex flex-col p-4 md:p-8 gap-6 bg-kokimiko-fondo/30 w-full overflow-x-hidden font-sans">
            <Toaster position="top-center" />
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 gap-4">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="p-4 bg-kokimiko-azul rounded-2xl text-white shadow-md"><FaBoxes size={24} /></div>
                    <div>
                        <h1 className="text-xl md:text-2xl font-black text-kokimiko-texto uppercase tracking-widest">Almacén Kokimiko</h1>
                        <p className="text-xs font-bold text-gray-400 mt-1 uppercase tracking-widest">Valor: <span className="text-kokimiko-azul">{formatCurrency(valorTotalInventario)}</span></p>
                    </div>
                </div>
                
                <button onClick={() => openModal()} className="w-full md:w-auto px-6 py-3 text-sm font-black rounded-xl text-white bg-kokimiko-texto hover:bg-kokimiko-azul transition-colors flex items-center justify-center gap-2 shadow-md uppercase tracking-widest">
                    <FaPlus /> Añadir Artículo
                </button>
            </div>

            <div className={`${cardStyle} flex-1 overflow-hidden flex flex-col`}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 border-b border-gray-100 pb-4 gap-4">
                    <div className="flex items-center gap-2 text-gray-400">
                        <FaSearch size={14} />
                        <h3 className="text-xs font-black uppercase tracking-widest">Buscar en almacén</h3>
                    </div>
                    <input
                        type="text"
                        placeholder="Escribe el nombre del artículo..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className={`${inputStyle} w-full sm:w-96 text-sm`}
                    />
                </div>

                {cargando ? (
                    <div className="flex-1 flex flex-col justify-center items-center">
                        <ClipLoader color="#1BB1A5" size={40} />
                        <p className="text-gray-400 mt-4 text-xs font-bold uppercase tracking-widest">Contando Inventario...</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto custom-scrollbar flex-1">
                        <table className="w-full text-sm text-left">
                            <thead className="text-[10px] text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100 font-black">
                                <tr>
                                    <th scope="col" className="px-6 py-4 rounded-tl-xl">Nombre del Artículo</th>
                                    <th scope="col" className="px-6 py-4 text-center">Stock Físico</th>
                                    <th scope="col" className="px-6 py-4 text-center">Límite Mín.</th>
                                    <th scope="col" className="px-6 py-4 text-right">Valor Ud.</th>
                                    <th scope="col" className="px-6 py-4 text-center rounded-tr-xl">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inventarioFiltrado.length > 0 ? (
                                    inventarioFiltrado.map(item => {
                                        const bajoStock = (item.stockMinimo > 0) && (item.cantidad <= item.stockMinimo);
                                        return (
                                            <tr key={item.id} className={`border-b border-gray-50 transition-colors ${bajoStock ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-kokimiko-azul/5'}`}>
                                                <td className="px-6 py-4">
                                                    <p className="font-black text-kokimiko-texto text-base">{item.nombre}</p>
                                                    {renderCategoriaBadge(item.categoria)}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`font-black text-lg ${bajoStock ? 'text-red-500' : 'text-gray-600'}`}>
                                                        {item.cantidad}
                                                    </span>
                                                    {bajoStock && <FaExclamationTriangle className="inline ml-2 text-red-500 pb-1" title="¡Stock bajo!" size={18}/>}
                                                </td>
                                                <td className="px-6 py-4 text-center font-bold text-gray-400">
                                                    {item.stockMinimo || 0}
                                                </td>
                                                <td className="px-6 py-4 text-right font-black text-kokimiko-teal text-base">
                                                    {formatCurrency(item.precio)}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex justify-center items-center gap-2">
                                                        <button onClick={() => openModal(item)} className="p-2 text-gray-400 hover:text-kokimiko-azul hover:bg-kokimiko-azul/10 rounded-lg transition-colors" title="Editar">
                                                            <FaEdit size={16} />
                                                        </button>
                                                        <button onClick={() => handleDeleteItem(item.id, item.categoria)} className="p-2 text-gray-400 hover:text-white hover:bg-red-500 rounded-lg transition-colors" title="Eliminar">
                                                            <FaTrash size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-12 text-gray-400 font-bold text-xs uppercase tracking-widest">
                                            El almacén está vacío
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Modal isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)} style={customModalStyles}>
                <ItemFormModal item={editingItem} onClose={() => setIsModalOpen(false)} onSave={handleSaveItem} />
            </Modal>
        </div>
    );
}

export default InventarioView;